import React, { useState, useEffect } from "react";
import { StyleSheet, View } from "react-native";
import {
  AnchorModel,
  ShareModel,
  ContactModel,
  GroupModel,
  MemoryModel,
  MediaModel,
  PlaceModel,
  ICustomCardEntityConfig,
  formatCardTimestamp,
  formatDisplayDate,
} from "common247";
import { ShareSelector } from "../sharing/ShareSelector";
import { ThemedKebabMenu, type IKebabActionItem } from "../ThemedKebabMenu";
import { ThemedListViewMode } from "../ThemedList";
import { ThemedChip } from "../ThemedChip";
import { ThemedCompactCard } from "../cards/ThemedCompactCard";
import { ThemedMediumCard } from "../cards/ThemedMediumCard";
import { ThemedFullCard } from "../cards/ThemedFullCard";
import { ThemedCustomCard } from "../cards/ThemedCustomCard";
import {
  resolveCardLinkedContacts,
  resolveCardLinkedGroups,
  resolveCardLinkedMedia,
  resolveCardLinkedMemories,
  resolveCardLinkedPlaces,
  resolveCardLinkedAnchors,
} from "../cards/card-helpers";

/**
 * Props for the AnchorCard component.
 */
export interface IAnchorCardProps {
  /** The Anchor data model */
  anchor: AnchorModel;
  /** Whether tag editing is permitted */
  canEditTags?: boolean;
  /** Whether to show the owner display name */
  showOwner?: boolean;
  /** Whether this is an administrative view */
  isAdminView?: boolean;
  /** Current authenticated user ID */
  currentUserId?: string;
  /** Compact density override */
  compact?: boolean;
  /** Presentation tier: chip, compact, medium, full, or custom */
  viewMode?: ThemedListViewMode;
  /** Hover state indicator */
  isHovered?: boolean;
  /** Selection mode */
  selectionMode?: boolean;
  /** Whether this card is selected */
  isSelected?: boolean;
  /** Primary card press callback */
  onPress?: () => void;
  /** Long press callback */
  onLongPress?: () => void;
  /** Web context menu callback */
  onContextMenu?: (e?: any) => void;
  /** Edit action callback */
  onEditPress?: () => void;
  /** Inspect action callback */
  onInspect?: () => void;
  /** Share action callback */
  onShare?: () => void;
  /** Delete action callback */
  onDelete?: () => void;

  /** Existing shares associated with this anchor */
  shares?: ShareModel[];
  /** Available contacts for sharing picker */
  availableContacts?: ContactModel[];
  /** Available groups for sharing picker */
  availableGroups?: GroupModel[];
  /** Available memories */
  availableMemories?: MemoryModel[];
  /** Available media */
  availableMedia?: MediaModel[];
  /** Available places */
  availablePlaces?: PlaceModel[];
  /** Available anchors */
  availableAnchors?: AnchorModel[];
  /** Loading shares flag */
  isLoadingShares?: boolean;
  /** Callback to share with a target */
  onShareTarget?: (targetId: string, targetType: "contact" | "group", role: "viewer" | "editor") => void;
  /** Callback to revoke a share */
  onUnshare?: (shareId: string) => void;
  /** Controlled sharing tray expansion state */
  isSharingExpanded?: boolean;
  /** Toggle sharing tray callback */
  onToggleSharing?: () => void;
  /** Custom card entity configuration */
  config?: ICustomCardEntityConfig;
}

/**
 * First-class AnchorCard component adhering to the 5-tier card architecture.
 *
 * Features:
 * - chip: seed green chip token
 * - compact: cover thumbnail, title, address snippet, favourite rating
 * - medium: compact + date, location coordinates badge, linked chips tray
 * - full: partitioned ledger folio with complete details and auto-expanded media highlights
 * - custom: dynamic schema-driven card based on ICustomCardEntityConfig
 */
export const AnchorCard: React.FC<IAnchorCardProps> = ({
  anchor,
  canEditTags,
  showOwner = false,
  isAdminView = false,
  currentUserId,
  compact = false,
  viewMode,
  isHovered,
  selectionMode = false,
  isSelected = false,
  onPress,
  onLongPress,
  onContextMenu,
  onEditPress,
  onInspect,
  onShare,
  onDelete,
  shares,
  availableContacts,
  availableGroups,
  availableMemories,
  availableMedia,
  availablePlaces,
  availableAnchors,
  isLoadingShares,
  onShareTarget,
  onUnshare,
  isSharingExpanded,
  onToggleSharing,
  config,
}) => {
  const userRole = anchor.userRole || "owner";
  const isOwner = userRole === "owner";
  const isViewer = userRole === "viewer";
  const canEdit = isOwner || userRole === "editor";
  const isFiveStar = anchor.favouriteRating === 5;

  const [localExpanded, setLocalExpanded] = useState(false);
  const isExpanded = isSharingExpanded !== undefined ? isSharingExpanded : localExpanded;

  const [lastKnownShares, setLastKnownShares] = useState<ShareModel[] | null>(null);

  useEffect(() => {
    if (shares !== undefined) {
      setLastKnownShares(shares);
    }
  }, [shares]);

  const handleSharePress = (e?: any) => {
    if (e?.stopPropagation) e.stopPropagation();
    if (onToggleSharing) {
      onToggleSharing();
    } else if (onShareTarget || shares !== undefined) {
      setLocalExpanded(!localExpanded);
    } else if (onShare) {
      onShare();
    } else {
      setLocalExpanded(!localExpanded);
    }
  };

  const effectiveShares: ShareModel[] =
    shares ??
    lastKnownShares ??
    (anchor.sharedWith?.map((s) => ({
      id: s.id,
      sourceId: anchor.id || "",
      sourceType: "anchor" as const,
      targetId: s.id,
      targetType: s.type,
      role: s.role,
      targetDisplayName: s.displayName,
      targetUserId: s.targetUserId,
      sharedBy: anchor.ownerId || "",
      sharedAt: anchor.createdAt,
    })) as ShareModel[]) ??
    [];

  const canShowShareButton = anchor.canShare && (onShare || onShareTarget || onToggleSharing || shares !== undefined);

  const kebabActions: IKebabActionItem[] = [];
  if (canShowShareButton) {
    kebabActions.push({
      id: "share",
      label: isExpanded ? "Close Sharing" : "Manage Sharing",
      icon: "share-social-outline",
      onPress: handleSharePress,
    });
  }
  if (canEdit && onEditPress) {
    kebabActions.push({
      id: "edit",
      label: "Edit Anchor",
      icon: "pencil-outline",
      onPress: onEditPress,
    });
  }
  if (isOwner && onDelete) {
    kebabActions.push({
      id: "delete",
      label: "Delete Anchor",
      icon: "trash-outline",
      destructive: true,
      onPress: onDelete,
    });
  }

  // 1. Chip tier
  if (viewMode === "chip") {
    return (
      <View style={{ margin: 2 }}>
        <ThemedChip
          entityType="anchor"
          id={anchor.id}
          label={anchor.name || "Anchor"}
          isOwner={isOwner}
          isFiveStar={isFiveStar}
          onPress={onPress}
          onLongPress={onLongPress}
        />
      </View>
    );
  }

  const resolvedDate = anchor.date ? formatDisplayDate(anchor.date) : undefined;
  const formattedDate = resolvedDate || (anchor.createdAt ? `Created: ${formatCardTimestamp(anchor.createdAt)}` : undefined);

  const resolvedMemories = resolveCardLinkedMemories(anchor);
  const resolvedMedia = resolveCardLinkedMedia(anchor, availableMedia);
  const resolvedGroups = resolveCardLinkedGroups(anchor, availableGroups);
  const resolvedContacts = resolveCardLinkedContacts(anchor, availableContacts);
  const resolvedPlaces = resolveCardLinkedPlaces(anchor, availablePlaces);
  const resolvedAnchors = resolveCardLinkedAnchors(anchor, availableAnchors);

  // Subtitle with type and address/location
  const typeLabel = anchor.type ? anchor.type.charAt(0).toUpperCase() + anchor.type.slice(1) : undefined;
  const addressSubtitle = anchor.address
    ? anchor.address
    : anchor.latitude !== undefined && anchor.longitude !== undefined
    ? `📍 ${anchor.latitude.toFixed(4)}, ${anchor.longitude.toFixed(4)}`
    : undefined;

  // 2. Medium tier
  if (viewMode === "medium") {
    return (
      <ThemedMediumCard
        entityType="anchor"
        id={anchor.id}
        title={anchor.name || "Anchor"}
        formattedDate={formattedDate}
        description={anchor.description || addressSubtitle}
        ownerDisplayName={anchor.ownerDisplayName}
        isOwner={isOwner}
        isFiveStar={isFiveStar}
        isShared={!isOwner}
        kebabActions={kebabActions}
        selectionMode={selectionMode}
        isSelected={isSelected}
        onPress={onPress}
        onLongPress={onLongPress}
        onContextMenu={onContextMenu}
        contactsList={resolvedContacts}
        groupsList={resolvedGroups}
        memoriesList={resolvedMemories}
        mediaList={resolvedMedia}
        placesList={resolvedPlaces}
        tagsList={anchor.tags ? Object.values(anchor.tags) : []}
        sharesList={effectiveShares}
      >
        {isExpanded && canShowShareButton && (
          <View style={styles.sharingTray}>
            <ShareSelector
              shares={effectiveShares}
              availableContacts={availableContacts as any}
              availableGroups={availableGroups}
              isLoading={isLoadingShares}
              onShare={onShareTarget || (() => {})}
              onUnshare={onUnshare || (() => {})}
              canEdit={canEdit}
            />
          </View>
        )}
      </ThemedMediumCard>
    );
  }

  // 3. Full tier
  if (viewMode === "full") {
    return (
      <View style={styles.fullCardWrapper}>
        <ThemedFullCard
          entityType="anchor"
          id={anchor.id}
          title={anchor.name || "Anchor"}
          formattedDate={formattedDate}
          description={anchor.description}
          ownerDisplayName={anchor.ownerDisplayName}
          isOwner={isOwner}
          isFiveStar={isFiveStar}
          userRole={userRole}
          kebabActions={kebabActions}
          onPress={onPress}
          onEditPress={onEditPress}
          onDeletePress={onDelete}
          contactsList={resolvedContacts}
          groupsList={resolvedGroups}
          memoriesList={resolvedMemories}
          mediaList={resolvedMedia}
          placesList={resolvedPlaces}
          tagsList={anchor.tags ? Object.values(anchor.tags) : []}
          sharesList={effectiveShares}
        />
        {isExpanded && canShowShareButton && (
          <View style={styles.sharingTray}>
            <ShareSelector
              shares={effectiveShares}
              availableContacts={availableContacts as any}
              availableGroups={availableGroups}
              isLoading={isLoadingShares}
              onShare={onShareTarget || (() => {})}
              onUnshare={onUnshare || (() => {})}
              canEdit={canEdit}
            />
          </View>
        )}
      </View>
    );
  }

  // 4. Custom tier
  if (viewMode === "custom") {
    return (
      <ThemedCustomCard
        entityType="anchor"
        item={anchor as any}
        title={anchor.name || "Anchor"}
        dateStr={formattedDate || ""}
        description={anchor.description || addressSubtitle || ""}
        isOwner={isOwner}
        isFiveStar={isFiveStar}
        kebabActions={kebabActions}
        selectionMode={selectionMode}
        isSelected={isSelected}
        onPress={onPress}
        onLongPress={onLongPress}
        onContextMenu={onContextMenu}
        placesList={resolvedPlaces}
        config={config}
      />
    );
  }

  // 5. Compact tier (default)
  return (
    <ThemedCompactCard
      entityType="anchor"
      id={anchor.id}
      title={anchor.name || "Anchor"}
      date={formattedDate}
      isOwner={isOwner}
      isFiveStar={isFiveStar}
      selectionMode={selectionMode}
      isSelected={isSelected}
      onPress={onPress}
      onLongPress={onLongPress}
      onContextMenu={onContextMenu}
      kebabActions={kebabActions}
    />
  );
};

const styles = StyleSheet.create({
  fullCardWrapper: {
    marginBottom: 8,
  },
  sharingTray: {
    padding: 12,
    marginTop: 8,
    borderRadius: 8,
  },
});
